import { type HTMLAttributes } from 'react';
import clsx from 'clsx';

import { SVGIcon } from '../icons/SVGIcon';

import styles from './progress.module.scss';

export type ProgressTextGenerator = (
  completedItems: number,
  totalItems: number
) => string;

type ProgressClassNames = {
  root?: string;
  section?: string;
};

export type ProgressProps = HTMLAttributes<HTMLDivElement> & {
  classNames?: ProgressClassNames;
  completedItems: number;
  getProgressText?: ProgressTextGenerator;
  totalItems: number;
  svgIconName?: string;
};

const defaultProgressText: ProgressTextGenerator = (
  completedItems,
  totalItems
) => `${completedItems} of ${totalItems}`;

export const Progress = ({
  className,
  classNames,
  completedItems,
  svgIconName,
  getProgressText = defaultProgressText,
  totalItems,
  ...divProps
}: ProgressProps) => {
  const progressValue =
    totalItems > 0
      ? Math.max(0, Math.min((completedItems / totalItems) * 100, 100))
      : 0;
  const progressText = getProgressText(completedItems, totalItems);

  return (
    <div className={styles['container']}>
      <div
        {...divProps}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progressValue)}
        className={clsx(styles['progress-root'], classNames?.root, className)}
      >
        <div
          className={clsx(styles['progress-section'], classNames?.section)}
          style={{ width: `${progressValue}%` }}
        />
      </div>
      <span className={styles['text']}>{progressText}</span>
      {svgIconName && <SVGIcon name={svgIconName} />}
    </div>
  );
};
